'use client';

import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function PdfLoader() {
  const { t } = useTranslation('interactions');

  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
        <span className="text-zinc-600 dark:text-zinc-400">
          {t('pdf.loading')}
        </span>
      </div>
    </div>
  );
}

// Dynamically import the PDF viewer to avoid SSR issues with pdfjs-dist
const PdfViewerInner = dynamic(() => import('./pdf-viewer-inner'), {
  ssr: false,
  loading: () => <PdfLoader />,
});

/**
 * Resolve the URL to hand to pdf.js.
 *
 * `/api/{chpl|ulotka}/{id}?format=json` says whether the file lives in
 * Vercel Blob. If so, pdf.js gets the absolute Blob URL and streams ranges
 * straight from the CDN. Otherwise (registry-only PDFs) it keeps the API
 * route, which proxies the file.
 *
 * We deliberately do not follow the route's 307 with fetch(): Sentry adds
 * tracing headers to same-origin fetches, the redirect carries them to Blob,
 * and Blob rejects the resulting CORS preflight. pdf.js 3.x also sends range
 * requests to the URL it was given, not the redirect target, so a redirecting
 * URL would re-enter the API route for every chunk.
 */
function useResolvedPdfUrl(fileUrl: string): string | null {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setResolvedUrl(null);

    // Warm the pdf.js chunk while the source lookup is in flight.
    void import('./pdf-viewer-inner');

    const separator = fileUrl.includes('?') ? '&' : '?';

    fetch(`${fileUrl}${separator}format=json`)
      .then(async (res) => {
        if (!res.ok) return fileUrl;

        const body = (await res.json()) as { kind?: string; url?: string };

        return body.kind === 'blob' && body.url ? body.url : fileUrl;
      })
      .catch(() => fileUrl)
      .then((url) => {
        if (!cancelled) setResolvedUrl(url);
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  return resolvedUrl;
}

interface PdfViewerProps {
  fileUrl: string;
  initialPage?: number;
  downloadUrl?: string;
}

export function PdfViewer({
  fileUrl,
  initialPage = 1,
  downloadUrl,
}: PdfViewerProps) {
  const resolvedUrl = useResolvedPdfUrl(fileUrl);

  if (!resolvedUrl) {
    return <PdfLoader />;
  }

  return (
    <PdfViewerInner
      fileUrl={resolvedUrl}
      initialPage={initialPage}
      downloadUrl={downloadUrl}
    />
  );
}
